import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME_REFERENCE_BOOK } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { EconomicSector, EconomicSectorRequestBody } from './types';

export const economicSectorsApi = createApi({
  reducerPath: 'economicSectorsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/economic-sectors`,
  }),
  tagTypes: ['EconomicSector', 'EconomicSectors'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME_REFERENCE_BOOK,
  endpoints: builder => ({
    getEconomicSectors: builder.query<EconomicSector[], void>({
      query: () => ({
        url: '/',
      }),
      providesTags: () => ['EconomicSector', 'EconomicSectors'],
    }),
    getEconomicSectorsI: builder.infiniteQuery<
      EconomicSector[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['EconomicSector', 'EconomicSectors'],
    }),
    getEconomicSectorById: builder.query<EconomicSector, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['EconomicSector'],
    }),
    createEconomicSector: builder.mutation<
      EconomicSector,
      EconomicSectorRequestBody
    >({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'EconomicSector',
        { type: 'EconomicSector', id: result?.id },
      ],
    }),
    updateEconomicSector: builder.mutation<
      EconomicSector,
      EconomicSectorRequestBody
    >({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'EconomicSector',
        { type: 'EconomicSector', id: result?.id },
      ],
    }),
    deleteEconomicSectorById: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'EconomicSector',
        { type: 'EconomicSector', id },
      ],
    }),
  }),
});

export const {
  useGetEconomicSectorsQuery,
  useGetEconomicSectorByIdQuery,
  useCreateEconomicSectorMutation,
  useUpdateEconomicSectorMutation,
  useDeleteEconomicSectorByIdMutation,
} = economicSectorsApi;

export const useGetEconomicSectorsIInfiniteQuery = normalizeInfiniteQueryData(
  economicSectorsApi.useGetEconomicSectorsIInfiniteQuery,
);
